import AddLink from '@mui/icons-material/AddLink'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import LaunchIcon from '@mui/icons-material/Launch'
import LinkOff from '@mui/icons-material/LinkOff'
import NearbyErrorIcon from '@mui/icons-material/NearbyError'
import SyncProblemIcon from '@mui/icons-material/SyncProblem'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import Realisatie from '@components/Realisatie'
import { Status } from '@components/Status'
import type { Entity } from '@models/Entity'

import type { Project } from '../../../aansluiting/project/types/Project'
import type { HoofdleidingProject } from '../../../hoofdleiding/project/types/HoofdleidingProject'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { StyledIconLink } from '../../../theme/components/StyledIconLink'
import type { Styles } from '../../../theme/types/Styles'

export { LinkedProjectRow }

type Props = {
  entity: Entity
  canUnlink: boolean
  canLink: boolean
  project: Project | HoofdleidingProject
  openUnlinkDialog: (id: string) => void
  openLinkDialog: () => void
  sx?: Styles
}

const LinkedProjectRow = ({
  project,
  entity,
  canUnlink,
  canLink,
  openUnlinkDialog,
  openLinkDialog,
  sx = {},
}: Props) => {
  const isCurrentProject = project.aanvraagID === entity.id
  const planningConflicts = project.acties?.planningConflict || []
  const planningConflictsHl = project.acties?.planningConflictHoofdleiding || []
  const planningConflictsAl = project.acties?.planningConflictAansluiting || []

  return (
    <TableRow sx={sx}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{project.aanvraagID}</Typography>

          {isCurrentProject && <Chip label="Huidig project" size="small" />}
        </Box>

        {'naam' in project && (
          <Typography
            variant="caption"
            sx={{ display: 'inline-block', marginTop: 0.5 }}
          >
            {project.naam}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {project.geplandeUitvoeringsdatum ? (
            <Realisatie
              date={project.geplandeUitvoeringsdatum}
              type={project.geplandeUitvoeringsdatumType}
              showWeek
            />
          ) : (
            project.startUitvoeringWeek
          )}

          {!project.isActive || project.isDirty ? (
            <Status project={project} />
          ) : (
            <>
              {planningConflictsHl.length > 0 && (
                <Tooltip
                  title={`Planning conflict hoofdleiding: ${planningConflictsHl.join(
                    ', ',
                  )}`}
                >
                  <Badge
                    badgeContent={planningConflictsHl.length}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: 'rgb(61, 108, 180)',
                        color: 'white',
                      },
                    }}
                    overlap="circular"
                  >
                    <IconButton sx={{ cursor: 'default' }}>
                      <NearbyErrorIcon />
                    </IconButton>
                  </Badge>
                </Tooltip>
              )}

              {planningConflictsAl.length > 0 && (
                <Tooltip
                  title={`Planning conflict aansluiting: ${planningConflictsAl.join(
                    ', ',
                  )}`}
                >
                  <Badge
                    badgeContent={planningConflictsAl.length}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: 'rgb(61, 108, 180)',
                        color: 'white',
                      },
                    }}
                    overlap="circular"
                  >
                    <IconButton sx={{ cursor: 'default' }}>
                      <SyncProblemIcon />
                    </IconButton>
                  </Badge>
                </Tooltip>
              )}

              {planningConflicts.length > 0 && (
                <Tooltip
                  title={`Planning conflict werkvoorbereiding: ${planningConflicts.join(
                    ', ',
                  )}`}
                >
                  <Badge
                    badgeContent={planningConflicts.length}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: 'rgb(61, 108, 180)',
                        color: 'white',
                      },
                    }}
                    overlap="circular"
                  >
                    <IconButton sx={{ cursor: 'default' }}>
                      <EventBusyIcon />
                    </IconButton>
                  </Badge>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </TableCell>

      <TableCell>{project.doorlooptijdWeken}</TableCell>

      <TableCell>{project.netbeheerders?.join(', ')}</TableCell>

      <TableCell>{project.aannemers?.join(', ')}</TableCell>

      <TableCell>{project.statusText}</TableCell>

      <TableCell>
        {canLink && (
          <StyledIconButton
            title={`Koppel een project aan ${entity.id}`}
            icon={<AddLink />}
            onClick={openLinkDialog}
          />
        )}

        {canUnlink && (
          <StyledIconButton
            icon={<LinkOff />}
            title={`Ontkoppel project ${project.aanvraagID} van ${entity.id}`}
            onClick={() => openUnlinkDialog(project.aanvraagID)}
          />
        )}

        {!isCurrentProject && (
          <StyledIconLink
            href={`/${
              project.type !== 'Hoofdleiding' ? 'aansluiting' : 'hoofdleiding'
            }/project/${project.aanvraagID}`}
            icon={<LaunchIcon />}
            title={`Navigeer naar project ${project.aanvraagID}`}
          />
        )}
      </TableCell>
    </TableRow>
  )
}
